import {useTheme, css} from '@mui/material';
import {GetStylesFunction} from '@wallaby-ui/core';
import {ICellRendererParams} from 'ag-grid-community';
import * as React from 'react';

import {useFormatters} from '../light';
import {TooltipWrapper} from '../TooltipWrapper';

type OwnProps = {
  underlined?: boolean;
  noWrap?: boolean;
  noTranslate?: boolean;
  style?: React.CSSProperties;
  /** Render the bare chip. For callers that already wrap it in a TooltipWrapper of their own — two
   * nested wrappers open both poppers on the same hover. */
  noTooltip?: boolean;
};

type Props = ICellRendererParams & OwnProps;

export const HaidiHaiTypeFormatter: React.FC<Props> = (props) => {
  const {value, data, underlined, noWrap, noTranslate, style, noTooltip} = props;

  const theme = useTheme();
  const styles = getStyles({theme});
  const {tEnum} = useFormatters();
  const standardTypeInfo = data.standardTypeCodeLabel ? ` (${data.standardTypeCodeLabel})` : '';
  let valueLabel = noTranslate ? value : tEnum('HaiTypeEnum', value) ?? '';
  if (value === 'BSI') {
    valueLabel = valueLabel.replace(/\s*\([^)]*\)/g, '').trim();
  }

  const tooltipContent = React.useMemo(() => {
    if (data.standardTypeCodeLabel && data.standardTypeTranslation) {
      return `${valueLabel}\n\n${data.standardTypeCodeLabel} - ${data.standardTypeTranslation}`;
    }
    return valueLabel;
  }, [valueLabel, data.standardTypeCodeLabel, data.standardTypeTranslation]);

  const colors = getHaiTypeColor(data);

  const chip = (
    <div
      data-name='hai-type-formatter'
      css={styles.chip}
      style={{
        color: colors?.text,
        background: colors?.background,
        textDecoration: underlined ? 'underline' : undefined,
        ...(style ?? {}),
      }}
    >
      <div css={styles.horizontalPadding} />
      <span
        css={styles.text}
        style={
          noWrap
            ? {
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
              }
            : undefined
        }
      >
        {valueLabel}
        {standardTypeInfo}
      </span>
      <div css={styles.horizontalPadding} />
    </div>
  );

  return noTooltip ? (
    chip
  ) : (
    <TooltipWrapper tooltip={tooltipContent} noPadding>
      {chip}
    </TooltipWrapper>
  );
};

export function getHaiTypeColor(data) {
  /*
      "enum.haiTypeEnum.bsi": "Krevní řečiště (primární)",
      "enum.haiTypeEnum.bsiS": "Krevní řečiště (sekundární)",
      "enum.haiTypeEnum.cri": "Související s žilním katetrem",
      "enum.haiTypeEnum.gi": "Gastrointestinální trakt",
      "enum.haiTypeEnum.hai": "Nespecifikovaná",
      "enum.haiTypeEnum.lri": "Dolní cesty dýchací",
      "enum.haiTypeEnum.other": "Jiná",
      "enum.haiTypeEnum.pn": "Pneumonie",
      "enum.haiTypeEnum.ssi": "Místo chir. výkonu",
      "enum.haiTypeEnum.uri": "Horní cesty dýchací",
      "enum.haiTypeEnum.uti": "Močové cesty",
     */
  const COLOR_MAP: Record<string, {text: string; background: string}> = {
    BSI: {text: 'var(--dlAgGridFontColor)', background: '#FFD6D6'},
    BSI_S: {text: 'var(--dlAgGridFontColor)', background: '#FFD6D6'},
    CRI: {text: 'var(--dlAgGridFontColor)', background: '#FFDAE9'},
    GI: {text: 'var(--dlAgGridFontColor)', background: '#FFEDD2'},
    HAI: {text: 'var(--dlAgGridFontColor)', background: '#E2E8F2'},
    LRI: {text: 'var(--dlAgGridFontColor)', background: '#EFDCFF'},
    OTHER: {text: 'var(--dlAgGridFontColor)', background: '#D8FFF8'},
    PN: {text: 'var(--dlAgGridFontColor)', background: '#CBC7FF'},
    SSI: {text: 'var(--dlAgGridFontColor)', background: '#EDFFA5'},
    URI: {text: 'var(--dlAgGridFontColor)', background: '#E1F7FF'},
    UTI: {text: 'var(--dlAgGridFontColor)', background: '#CDFBE0'},
    INITIAL: {text: 'var(--dlAgGridFontColor)', background: '#E2E8F2'},
  };
  const REJECTED_COLORS = {
    text: 'var(--dlAgGridFontColor)',
    background: '#F4F6FA',
  };
  let colors = COLOR_MAP?.[data.type] ?? {};
  if (data.state === 'NO') {
    colors = REJECTED_COLORS;
  }

  return colors;
}

HaidiHaiTypeFormatter.displayName = 'HaidiHaiTypeFormatter';

const getStyles: GetStylesFunction = (options) => ({
  chip: css`
    max-width: 100%;
    border-radius: 4px;
    padding: 3px 0;
    display: inline-flex;
    justify-content: flex-start;
    align-items: flex-start;
  `,
  horizontalPadding: css`
    width: 8px;
    min-width: 8px;
    height: 1em;
    min-height: 1em;
  `,
  text: css`
    border-radius: 4px;
    margin: 0;
    font-family: SourceSansPro-Regular;
    font-size: 14px;
    line-height: 18px;
    text-wrap: wrap;
    word-break: break-word;
    text-align: left;
  `,
});
